import React, { useEffect } from 'react'

import { useAuthentication } from '../../hooks/useAuthentication'
import { useQuery } from '@apollo/react-hooks'
import { DataTable } from '../table/DataTable'
import { GET_INBOX } from './gql'
import { getInboxColumns } from '../table/columns/inboxColumns'
import { useLocation, useHistory } from 'react-router-dom'
import { Alert, Box, Typography } from '@mui/material'
import MailIcon from '@mui/icons-material/Mail'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import Loader from '../Loader'
import PageHeader from '../Common/PageHeader'
import VoucherExpandedRow from '../table/VoucherExpandedRow'

const InboxVouchers = () => {
  const { authState } = useAuthentication()
  const vouchers = useQuery(GET_INBOX, {
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'network-only', // always fetch fresh data
  })
  const location = useLocation<{ refresh?: boolean }>()
  const history = useHistory()

  useEffect(() => {
    if (location.state?.refresh) {
      vouchers.refetch()
      // Clear the refresh flag from location state to prevent infinite loop
      history.replace(location.pathname, {})
    }
  }, [location.state, location.pathname, history])

  const columns = React.useMemo(() => getInboxColumns(), [])

  if (vouchers.loading && !vouchers.data) return <Loader />
  if (vouchers.error) return <p>Error: {vouchers.error.message}</p>
  if (!vouchers.data || !vouchers.data.inbox) return <p>No data available</p>

  const inboxVouchers = vouchers.data.inbox ?? []

  return (
    <>
      {location.state && (location.state as { message?: string }).message && (
        <>
          <br />
          <Alert variant="outlined" severity="success">
            {(location.state as { message?: string }).message}
          </Alert>
        </>
      )}
      <Box sx={{ width: '100%' }}>
        <PageHeader title="Inbox" icon={MailIcon} />
        {inboxVouchers.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 8,
              color: 'text.secondary',
            }}
          >
            <InboxOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              You're all caught up
            </Typography>
            <Typography variant="body2">
              Nothing is waiting on your approval right now.
            </Typography>
          </Box>
        ) : (
          <DataTable
            data={inboxVouchers}
            columns={columns}
            showGlobalSearch={false}
            showColumnFilters={false}
            showPagination={false}
            renderSubComponent={(row) => (
              <VoucherExpandedRow voucher={row.original} />
            )}
          />
        )}
      </Box>
    </>
  )
}

export default InboxVouchers
